import { MailOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useUpdateAgencyById } from '@nxsys/hooks/api/agency';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';

import { UpdateAgencyByIdParams } from '@nxsys/types/api';
import type { Option } from '@nxsys/types/models/Common';
import { IRELAND } from '@nxsys/store/constants/keys';
import { countryList } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { showMessage } from '@nxsys/utils/message';
import { validateEmail, validatePhone } from '@nxsys/utils/validator';
import type { FormProps } from 'antd';
import { Col, Divider, Form, Row, Space, Typography } from 'antd';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const ContactDetailFormWrapper = styled.div`
  .ant-form-item .ant-form-item-label {
    display: flex;
    align-items: center;
  }
  .ant-form-item .ant-form-item-label > label {
    min-height: unset;
  }
`;

export type ContactDetailFormValues = {
  contactName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  county?: string;
  postCode?: string;
  country?: Option<string> | null;
  mobile?: string;
  email?: string;
  emergencyContactName?: string;
  emergencyEmail?: string;
  emergencyTelephone?: string;
  emergencyMobile?: string;
};

export type ContactDetailRef = {
  submit: () => void;
};

type ContactDetailProps = {
  data?: IAgency | null;
  mode?: 'edit' | 'create';
  initialValues?: Partial<ContactDetailFormValues>;
  initialOnboardingEmails?: string[];
  onSubmit?: (
    values: ContactDetailFormValues,
    onboardingEmailNotification: string[],
  ) => void;
  hideActions?: boolean;
  ref?: React.Ref<ContactDetailRef>;
};

const defaultEmptyContactValues: ContactDetailFormValues = {
  contactName: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  county: '',
  postCode: '',
  country: null,
  mobile: '',
  email: '',
  emergencyContactName: '',
  emergencyEmail: '',
  emergencyTelephone: '',
  emergencyMobile: '',
};

const ContactDetail = forwardRef<ContactDetailRef, ContactDetailProps>(
  function ContactDetail(
    {
      data,
      mode = 'edit',
      initialValues: externalInitialValues,
      initialOnboardingEmails = [],
      onSubmit,
      hideActions = false,
    },
    ref,
  ) {
    const { messages } = useTypedIntl();
    const params = useParams();
    const agencyId = params.agencyId;
    const { payrollCountry } = useSelector((state: AppState) => state.appStore);
    const isIreland = payrollCountry === IRELAND;
    const isCreateMode = mode === 'create' || !data;

    const [form] = Form.useForm<ContactDetailFormValues>();
    const [resetKey, setResetKey] = useState(0);

    const [onboardingEmailValue, setOnboardingEmailValue] =
      useState<string>('');

    const onboardingEmailsKey = useMemo(() => {
      const emails = isCreateMode
        ? (initialOnboardingEmails ?? [])
        : (data?.onboardingEmailNotification ?? []);
      return emails.join('|');
    }, [
      data?.onboardingEmailNotification,
      initialOnboardingEmails,
      isCreateMode,
    ]);

    const onboardingEmails = useMemo(() => {
      if (!onboardingEmailsKey) return [];
      return onboardingEmailsKey.split('|').filter(Boolean);
    }, [onboardingEmailsKey]);

    const baseOnboardingEmailList = useMemo<Option<string>[]>(() => {
      return onboardingEmails.map((item) => ({
        label: item,
        value: item,
      }));
    }, [onboardingEmails]);

    const [onboardingEmailList, setOnboardingEmailList] = useState<
      Option<string>[]
    >(baseOnboardingEmailList);

    const { run: updateAgency, loading: isUpdating } = useUpdateAgencyById();

    const initialValues = useMemo<ContactDetailFormValues>(() => {
      if (isCreateMode) {
        return { ...defaultEmptyContactValues, ...externalInitialValues };
      }
      return {
        contactName: data?.contactName ?? '',
        address1: data?.address?.[0] ?? '',
        address2: data?.address?.[1] ?? '',
        address3: data?.address?.[2] ?? '',
        city: data?.city ?? '',
        county: data?.county ?? '',
        postCode: data?.postCode ?? '',
        country: data?.country
          ? {
              label:
                countryList.find((c) => c.value === data.country)?.label ?? '',
              value: data.country,
            }
          : null,
        mobile: data?.mobile ?? '',
        email: data?.email ?? '',
        emergencyContactName: data?.emergencyContactName ?? '',
        emergencyEmail: data?.emergencyEmail ?? '',
        emergencyTelephone: data?.emergencyTelephone ?? '',
        emergencyMobile: data?.emergencyMobile ?? '',
      };
    }, [
      data?.address,
      data?.city,
      data?.contactName,
      data?.country,
      data?.county,
      data?.email,
      data?.mobile,
      data?.postCode,
      data?.emergencyContactName,
      data?.emergencyEmail,
      data?.emergencyTelephone,
      data?.emergencyMobile,
      externalInitialValues,
      isCreateMode,
    ]);

    const [savedValues, setSavedValues] =
      useState<ContactDetailFormValues>(initialValues);
    const [savedEmailList, setSavedEmailList] = useState<Option<string>[]>(
      baseOnboardingEmailList,
    );

    useImperativeHandle(ref, () => ({
      submit: () => form.submit(),
    }));

    useEffect(() => {
      setSavedValues(initialValues);
      setSavedEmailList(baseOnboardingEmailList);
      setOnboardingEmailList(baseOnboardingEmailList);
      setOnboardingEmailValue('');
      setResetKey((k) => k + 1);
    }, [initialValues, baseOnboardingEmailList]);

    const handleAddOnboardingEmail = (email: string) => {
      if (!validateEmail(email)) {
        showMessage({
          type: 'error',
          content: messages['form.enterValidEmail'],
        });
        return;
      }
      if (onboardingEmailList.some((item) => item.value === email)) {
        showMessage({
          type: 'error',
          content: messages['common.emailAlreadyAdded'],
        });
        return;
      }
      setOnboardingEmailList([
        ...onboardingEmailList,
        { label: email, value: email },
      ]);
      setOnboardingEmailValue('');
    };

    const onFinish: FormProps<ContactDetailFormValues>['onFinish'] = (
      values,
    ) => {
      if (isCreateMode && onSubmit) {
        onSubmit(
          values,
          onboardingEmailList.map((x) => x.value),
        );
        return;
      }
      if (!agencyId) return;

      const params: UpdateAgencyByIdParams = {
        contactName: values.contactName ?? '',
        email: values.email || null,
        mobile: values.mobile || null,
        address: [
          values.address1 || '',
          values.address2 || '',
          values.address3 || '',
        ],
        city: values.city ?? '',
        county:
          typeof values.county === 'object'
            ? (values.county as any)?.value ?? ''
            : values.county ?? '',
        postCode: values.postCode ?? '',
        country: values.country?.value ?? '',
        onboardingEmailNotification: onboardingEmailList.map((x) => x.value),
        emergencyContactName: values.emergencyContactName || '',
        emergencyMobile: values.emergencyMobile || null,
        emergencyEmail: values.emergencyEmail || null,
        emergencyTelephone: values.emergencyTelephone || null,
      };

      updateAgency(agencyId, params).then(() => {
        showMessage({
          type: 'success',
          content: messages['prospect.agency.updateSuccess'],
        });

        const nextSavedValues: ContactDetailFormValues = {
          ...values,
          country: values.country ?? null,
        };
        setSavedValues(nextSavedValues);
        setSavedEmailList(onboardingEmailList);
      });
    };

    if (!isCreateMode && !data)
      return (
        <div className="relative h-[100px]">
          <AppLoader />
        </div>
      );

    return (
      <div className="ContactDetail  mx-auto">
        <div className="mx-auto w-full">
          <ContactDetailFormWrapper className="flex justify-center">
            <div className="max-w-[600px]">
              <div className="mb-3">
                <Typography.Text className="font-medium text-gray-800 text-lg">
                  {messages['common.contactDetails']}
                </Typography.Text>
              </div>

              <AppFormContainer
                key={resetKey}
                form={form}
                initialValues={savedValues}
                onFinish={onFinish}
                onFinishFailed={(errorInfo: any) => {
                  const fields = errorInfo?.errorFields
                    ?.map((f: any) => f.errors?.[0])
                    .filter(Boolean);
                  if (fields?.length) {
                    showMessage({
                      type: 'error',
                      content: fields.join('. '),
                    });
                  }
                }}
                scrollToFirstError
                layout="vertical"
              >
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      label={messages['common.contactName']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="contactName"
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterContactName']}
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        messages['common.addressLine1'] ??
                        messages['agency.create.address1']
                      }
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="address1"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterAddress1']}
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        messages['common.addressLine2'] ??
                        messages['agency.create.address2']
                      }
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="address2"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterAddress2']}
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        messages['common.addressLine3'] ??
                        messages['agency.create.address3']
                      }
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="address3"
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterAddress3']}
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['agency.create.city']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="city"
                    >
                      <AppInput
                        className="w-full"
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.county']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="county"
                    >
                      {isIreland ? (
                        <AppSelect
                          showSearch
                          options={irelandCountyOptions}
                          placeholder={messages['form.select']}
                          className="w-full"
                        />
                      ) : (
                        <AppInput
                          className="w-full"
                          placeholder={messages['form.enter']}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        messages[
                          isIreland ? 'common.eirCode' : 'common.postCode'
                        ] ??
                        messages['agency.create.postCode']
                      }
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="postCode"
                      rules={[
                        {
                          required: true,
                        },
                        postCodeValidationRule(messages, payrollCountry),
                      ]}
                    >
                      <AppInput
                        placeholder={
                        messages[
                          isIreland
                            ? 'agency.create.enterEirCode'
                            : 'agency.create.enterPostCode'
                        ]
                      }
                        upperCaseAll
                        className="w-full"
                        onChange={(e) => {
                          form.setFieldValue(
                            'postCode',
                            formatPostCode(e.target.value, payrollCountry),
                          );
                        }}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['agency.create.country']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="country"
                    >
                      <AppSelect
                        placeholder={messages['agency.create.selectCountry']}
                        options={countryList}
                        className="w-full !min-w-0"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.email']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="email"
                      rules={[
                        {
                          required: true,
                        },
                        {
                          type: 'email',
                          message: messages['form.enterValidEmail'],
                        },
                      ]}
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterEmail']}
                        allowCapitalize={false}
                        prefix={<MailOutlined className="text-gray-400" />}
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.phone']}
                      name="mobile"
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            if (!validatePhone(value)) {
                              return Promise.reject(
                                messages['form.enterValidMobile'],
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} className="mb-3 mt-6">
                    <Typography.Text className="font-medium text-gray-800 text-lg">
                      {messages['agency.create.onboardingEmail']}
                    </Typography.Text>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={messages['agency.create.onboardingEmail']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppSelect
                        options={[]}
                        placeholder={messages['agency.create.enterEmail']}
                        mode="multiple"
                        className="w-full !min-w-0"
                        open={false}
                        autoClearSearchValue={false}
                        allowClear={false}
                        allowCapitalize={false}
                        suffixIcon={
                          <PlusOutlined
                            onClick={() =>
                              handleAddOnboardingEmail(onboardingEmailValue)
                            }
                            style={{
                              color: defaultTheme.theme.palette.primary[600],
                            }}
                          />
                        }
                        searchValue={onboardingEmailValue}
                        onSearch={(value: string) =>
                          setOnboardingEmailValue(value)
                        }
                        value={onboardingEmailList}
                        onDeselect={(option: any) => {
                          setOnboardingEmailList(
                            onboardingEmailList.filter(
                              (item) => item.value !== option.value,
                            ),
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Divider className="my-4" />
                  </Col>

                  <Col xs={24} className="mb-3">
                    <Typography.Text className="font-medium text-gray-800 text-lg">
                      {messages['common.emergencyContact']}
                    </Typography.Text>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.contactName']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="emergencyContactName"
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterContactName']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.email']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="emergencyEmail"
                      rules={[
                        {
                          type: 'email',
                          message: messages['form.enterValidEmail'],
                        },
                      ]}
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterEmail']}
                        allowCapitalize={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.telephone']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="emergencyTelephone"
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterTelephone']}
                        onChange={(e) => {
                          form.setFieldValue(
                            'emergencyTelephone',
                            e.target.value.replace(/\D/g, ''),
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={messages['common.mobilePhone']}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name="emergencyMobile"
                    >
                      <AppInput
                        placeholder={messages['agency.create.enterMobile']}
                        onChange={(e) => {
                          form.setFieldValue(
                            'emergencyMobile',
                            e.target.value.replace(/\D/g, ''),
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </AppFormContainer>

              {!hideActions && (
                <div className="mt-4 flex">
                  <Space>
                    <AppButton
                      variant="outlined"
                      type="primary"
                      name={messages['common.cancel'] || 'Cancel'}
                      onClick={() => {
                        setOnboardingEmailList(savedEmailList);
                        setOnboardingEmailValue('');
                        setResetKey((k) => k + 1);
                      }}
                      disabled={isUpdating}
                    />
                    <AppButton
                      type="primary"
                      name={messages['common.save'] || 'Save'}
                      onClick={() => form.submit()}
                      loading={isUpdating}
                      disabled={isUpdating}
                    />
                  </Space>
                </div>
              )}
            </div>
          </ContactDetailFormWrapper>
        </div>
      </div>
    );
  },
);

export default ContactDetail;
